import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Drawer from "@mui/material/Drawer";
import Avatar from "@mui/material/Avatar";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Plus, Users as UsersIcon, Home as HomeIcon, Calendar as CalIcon, TrendingUp, Star,
  MapPin, Trash2, Pencil, Percent, LayoutDashboard, Menu as MenuIcon, Search, ShieldCheck,
  MessageCircle, FileText, Settings as SettingsIcon, Check, X, Send, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  PieChart, Pie, Cell,
} from "recharts";
import Eyebrow from "@/components/common/Eyebrow";

const STATUS_COLORS = {
  confirmed: { color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  approved: { color: "#16a34a", bg: "rgba(34,197,94,0.12)" },
  declined: { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.12)" },
};

const VILLA_STATUS_COLORS = {
  pending: { color: "#d97706", bg: "rgba(217,119,6,0.12)" },
  approved: { color: "#16a34a", bg: "rgba(34,197,94,0.12)" },
  rejected: { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
};

const NAV = [
  { value: "overview", label: "Overview", Icon: LayoutDashboard },
  { value: "locations", label: "Locations", Icon: MapPin },
  { value: "users", label: "Users", Icon: UsersIcon },
  { value: "villas", label: "Villas", Icon: HomeIcon },
  { value: "bookings", label: "Bookings", Icon: CalIcon },
  { value: "coupons", label: "Pricing & Offers", Icon: Percent },
  { value: "reviews", label: "Reviews", Icon: Star },
  { value: "inbox", label: "Support Inbox", Icon: MessageCircle },
  { value: "content", label: "Content", Icon: FileText },
  { value: "settings", label: "Settings", Icon: SettingsIcon },
];

function TabPanel({ value, current, children }) {
  if (value !== current) return null;
  return <Box sx={{ pt: 0 }}>{children}</Box>;
}

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.cancelled;
  return (
    <Chip
      label={status}
      size="small"
      data-testid={`status-chip-${status}`}
      sx={{
        color: c.color, bgcolor: c.bg, fontWeight: 600, fontSize: 11,
        textTransform: "uppercase", letterSpacing: "0.06em", height: 24,
      }}
    />
  );
}

function VillaStatusChip({ status }) {
  const c = VILLA_STATUS_COLORS[status] || VILLA_STATUS_COLORS.pending;
  return (
    <Chip
      label={status}
      size="small"
      data-testid={`villa-status-chip-${status}`}
      sx={{
        color: c.color, bgcolor: c.bg, fontWeight: 600, fontSize: 10,
        textTransform: "uppercase", letterSpacing: "0.06em", height: 20,
      }}
    />
  );
}

function EmptyState({ Icon, label, testId }) {
  return (
    <Box sx={{ py: 8, textAlign: "center" }} data-testid={testId}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: (t) => alpha(t.palette.text.primary, 0.05), display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
        <Icon size={22} className="text-gold" />
      </Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function ConfirmDelete({ label, onConfirm, testId }) {
  return (
    <Tooltip title="Delete">
      <IconButton
        size="small"
        onClick={() => { if (window.confirm(`Delete ${label}?`)) onConfirm(); }}
        data-testid={testId}
        sx={{ width: 32, height: 32, border: 1, borderColor: "error.main", color: "error.main", "&:hover": { bgcolor: alpha("#ef4444", 0.08) } }}
      >
        <Trash2 size={13} />
      </IconButton>
    </Tooltip>
  );
}

function EditButton({ onClick, testId }) {
  return (
    <Tooltip title="Edit">
      <IconButton size="small" onClick={onClick} data-testid={testId} sx={{ width: 32, height: 32, border: 1, borderColor: "divider" }}>
        <Pencil size={13} />
      </IconButton>
    </Tooltip>
  );
}

const CARD_SX = { borderRadius: 4, border: 0, boxShadow: 1 };

// ---------- App shell ----------

function Sidebar({ tab, onSelect, mobileOpen, onClose }) {
  const items = (
    <Box sx={{ width: 250, height: "100%", display: "flex", flexDirection: "column", bgcolor: "background.paper" }}>
      <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <ShieldCheck size={18} color="#fff" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }} noWrap>Nirvagoo</Typography>
          <Typography variant="caption" color="text.secondary">Admin console</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {NAV.map(({ value, label, Icon }) => {
          const active = tab === value;
          return (
            <Box
              key={value}
              component="button"
              type="button"
              onClick={() => { onSelect(value); onClose?.(); }}
              data-testid={`admin-nav-${value}`}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5, width: "100%",
                px: 2, py: 1.15, borderRadius: 2.5, border: "none", cursor: "pointer",
                bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.12) : "transparent",
                color: active ? "primary.main" : "text.primary",
                fontWeight: active ? 600 : 500, fontSize: 14, textAlign: "left", font: "inherit",
                transition: "background 0.15s ease",
                "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, active ? 0 : 0.05) },
              }}
            >
              <Icon size={16} />
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0, borderRight: 1, borderColor: "divider" }}>
        {items}
      </Box>
      <Drawer open={mobileOpen} onClose={onClose} ModalProps={{ keepMounted: true }} sx={{ display: { xs: "block", md: "none" } }}>
        {items}
      </Drawer>
    </>
  );
}

function Topbar({ title, onMenuClick, user }) {
  return (
    <Box sx={{ px: { xs: 2.5, md: 4 }, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderBottom: 1, borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <IconButton onClick={onMenuClick} data-testid="admin-menu-toggle" sx={{ display: { xs: "flex", md: "none" } }}>
          <MenuIcon size={18} />
        </IconButton>
        <Typography className="font-serif-display" sx={{ fontSize: { xs: 22, md: 28 } }} noWrap>{title}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 14 }}>
          {user.name?.[0]?.toUpperCase() || "A"}
        </Box>
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
          <Typography variant="caption" color="text.secondary">Administrator</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Overview ----------

function StatCard({ Icon, value, label, color }) {
  return (
    <Box sx={{ ...CARD_SX, p: 2.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2.5, bgcolor: alpha(color, 0.12), color, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
        <Icon size={17} />
      </Box>
      <Typography className="font-serif-display" sx={{ fontSize: 24 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mt: 0.5 }}>{label}</Typography>
    </Box>
  );
}

function OverviewPanel({ stats }) {
  const theme = useTheme();
  const gridColor = theme.palette.divider;
  const textColor = theme.palette.text.secondary;

  const STATS = [
    { Icon: UsersIcon, value: stats.users, label: "Users", color: "#8b5cf6" },
    { Icon: HomeIcon, value: stats.villas, label: "Villas", color: "#0ea5e9" },
    { Icon: CalIcon, value: stats.bookings, label: "Bookings", color: "#f59e0b" },
    { Icon: TrendingUp, value: `₹${stats.total_revenue.toLocaleString()}`, label: "Revenue", color: "#16a34a" },
    { Icon: Star, value: stats.avg_rating, label: "Avg rating", color: "#e11d48" },
    { Icon: Percent, value: stats.coupons, label: "Coupons", color: "#C5A059" },
  ];

  const statusData = Object.entries(stats.booking_status_counts || {})
    .map(([status, count]) => ({ status, count }))
    .filter((d) => d.count > 0);

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 2, mb: 3 }}>
        {STATS.map((s) => <StatCard key={s.label} {...s} />)}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 3 }}>
        <Box sx={{ ...CARD_SX, p: 3 }} data-testid="revenue-trend-chart">
          <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>Revenue trend</Typography>
          <Typography variant="caption" color="text.secondary">Last 6 months, confirmed &amp; approved bookings</Typography>
          <Box sx={{ height: 260, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.revenue_trend || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C5A059" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="label" stroke={textColor} fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke={textColor} fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} width={48} />
                <ChartTooltip
                  formatter={(v) => [`₹${Number(v).toLocaleString()}`, "Revenue"]}
                  contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13 }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#C5A059" strokeWidth={2} fill="url(#revenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box sx={{ ...CARD_SX, p: 3 }} data-testid="booking-status-chart">
          <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>Booking status</Typography>
          <Typography variant="caption" color="text.secondary">All-time breakdown</Typography>
          {statusData.length === 0 ? (
            <EmptyState Icon={CalIcon} label="No bookings yet" testId="no-status-data" />
          ) : (
            <>
              <Box sx={{ height: 180, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} dataKey="count" nameKey="status" innerRadius={45} outerRadius={70} paddingAngle={3}>
                      {statusData.map((d) => <Cell key={d.status} fill={STATUS_COLORS[d.status]?.color} />)}
                    </Pie>
                    <ChartTooltip contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {statusData.map((d) => (
                  <Box key={d.status} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_COLORS[d.status]?.color }} />
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>{d.status} ({d.count})</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Locations ----------

function LocationsPanel() {
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(null); // null | "new" | location
  const load = () => api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const empty = { name: "", region: "", description: "", image: "", featured: false };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (open && open !== "new") setForm({ ...empty, ...open });
    else setForm(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (open === "new") await api.post("/admin/locations", form);
      else await api.patch(`/admin/locations/${open.id}`, form);
      toast("Location saved");
      setOpen(null); load();
    } catch { toast("Could not save location"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/locations/${id}`); toast("Location deleted"); load(); }
    catch { toast("Could not delete"); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <Button variant="contained" startIcon={<Plus size={15} />} onClick={() => setOpen("new")} data-testid="new-location-btn">
          Add location
        </Button>
      </Box>
      {locations.length === 0 ? (
        <Box sx={CARD_SX}><EmptyState Icon={MapPin} label="No locations yet." testId="no-locations" /></Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 3 }}>
          {locations.map((l) => (
            <Box key={l.id} data-testid={`location-${l.id}`} sx={{ ...CARD_SX, overflow: "hidden", transition: "box-shadow 0.2s ease", "&:hover": { boxShadow: 3 } }}>
              {l.image ? (
                <Box sx={{ aspectRatio: "16 / 9", overflow: "hidden" }}><Box component="img" src={l.image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /></Box>
              ) : (
                <Box sx={{ aspectRatio: "16 / 9", bgcolor: (t) => alpha(t.palette.text.primary, 0.04), display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MapPin size={24} className="text-gold" />
                </Box>
              )}
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography className="font-serif-display" sx={{ fontSize: 18 }}>{l.name}</Typography>
                  {l.featured && <Chip label="Featured" size="small" sx={{ bgcolor: alpha("#C5A059", 0.15), color: "#C5A059", fontWeight: 600, fontSize: 10, height: 20 }} />}
                </Box>
                <Typography variant="caption" color="text.secondary">{l.region}</Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, mt: 1.5 }}>
                  <EditButton onClick={() => setOpen(l)} testId={`edit-location-${l.id}`} />
                  <ConfirmDelete label={l.name} onConfirm={() => remove(l.id)} testId={`delete-location-${l.id}`} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha("#C5A059", 0.15), color: "#C5A059", display: "flex", alignItems: "center", justifyContent: "center" }}><MapPin size={16} /></Box>
          <Typography className="font-serif-display" sx={{ fontSize: 22 }}>{open === "new" ? "Add location" : "Edit location"}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={save} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} inputProps={{ "data-testid": "location-name" }} fullWidth />
              <TextField label="Region" required value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} inputProps={{ "data-testid": "location-region" }} fullWidth />
            </Box>
            <TextField label="Description" value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} inputProps={{ "data-testid": "location-description" }} multiline rows={2} fullWidth />
            <TextField label="Image URL" value={form.image || ""} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} inputProps={{ "data-testid": "location-image" }} fullWidth />
            <FormControlLabel
              control={<Switch checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} inputProps={{ "data-testid": "location-featured" }} />}
              label="Featured"
            />
            <Button type="submit" variant="contained" size="large" data-testid="save-location-btn">Save location</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ---------- Users ----------

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    try { await api.patch(`/admin/users/${u.id}`, { role }); toast("Role updated"); load(); }
    catch { toast("Could not update role"); }
  };
  const remove = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast("User deleted"); load(); }
    catch { toast("Could not delete user"); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => [u.name, u.email, u.phone].filter(Boolean).some((v) => v.toLowerCase().includes(needle)));
  }, [users, q]);

  const roleColor = { admin: "#e11d48", owner: "#0ea5e9", user: "#8b5cf6" };

  return (
    <Box>
      <TextField
        placeholder="Search by name, email or phone…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2.5, maxWidth: 340 }}
        inputProps={{ "data-testid": "user-search" }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }}
      />
      <Box sx={{ ...CARD_SX, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
            <TableRow>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Name</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Email / Phone</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Role</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Joined</Eyebrow></TableCell>
              <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Actions</Eyebrow></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><EmptyState Icon={UsersIcon} label="No users found." testId="no-users" /></TableCell></TableRow>
            ) : filtered.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`} sx={{ "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: alpha(roleColor[u.role] || "#8b5cf6", 0.15), color: roleColor[u.role] || "#8b5cf6", fontWeight: 600 }}>
                      {u.name?.[0]?.toUpperCase() || "U"}
                    </Avatar>
                    {u.name}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{u.email || u.phone || "—"}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={u.role}
                    onChange={(e) => setRole(u, e.target.value)}
                    inputProps={{ "data-testid": `user-role-${u.id}` }}
                    sx={{ minWidth: 110 }}
                  >
                    <MenuItem value="user">user</MenuItem>
                    <MenuItem value="owner">owner</MenuItem>
                    <MenuItem value="admin">admin</MenuItem>
                  </Select>
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{format(new Date(u.created_at), "dd MMM yyyy")}</TableCell>
                <TableCell align="right"><ConfirmDelete label={u.name} onConfirm={() => remove(u)} testId={`delete-user-${u.id}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// ---------- Villas ----------

function VillasPanel() {
  const [villas, setVillas] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(null); // null | "new" | villa
  const load = () => api.get("/admin/villas").then((r) => setVillas(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setVillaStatus = async (v, status) => {
    try { await api.patch(`/admin/villas/${v.id}/status`, { status }); toast(`Villa ${status}`); load(); }
    catch { toast("Could not update status"); }
  };

  const empty = {
    name: "", location: "", destination_region: "", price_per_night: "", discount_percent: 0,
    bedrooms: "", bathrooms: "", max_guests: "", description: "", images: "", amenities: "", categories: "",
    pet_friendly: false, instant_book: true, featured: false,
  };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (open && open !== "new") {
      setForm({
        ...empty, ...open,
        images: (open.images || []).join(", "),
        amenities: (open.amenities || []).join(", "),
        categories: (open.categories || []).join(", "),
      });
    } else setForm(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_per_night: parseFloat(form.price_per_night),
      discount_percent: parseInt(form.discount_percent) || 0,
      bedrooms: parseInt(form.bedrooms), bathrooms: parseInt(form.bathrooms), max_guests: parseInt(form.max_guests),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
      amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
      categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (open === "new") await api.post("/admin/villas", payload);
      else await api.patch(`/admin/villas/${open.id}`, payload);
      toast("Villa saved"); setOpen(null); load();
    } catch { toast("Could not save villa"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/villas/${id}`); toast("Villa deleted"); load(); }
    catch { toast("Could not delete"); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return villas
      .filter((v) => statusFilter === "all" || v.status === statusFilter)
      .filter((v) => !needle || [v.name, v.location, v.destination_region].filter(Boolean).some((s) => s.toLowerCase().includes(needle)));
  }, [villas, q, statusFilter]);

  const pendingCount = villas.filter((v) => v.status === "pending").length;

  const fields = [
    ["name", "Villa name"], ["location", "Location (city, region)"], ["destination_region", "Destination region"],
    ["price_per_night", "Price per night (₹)"], ["discount_percent", "Discount %"],
    ["bedrooms", "Bedrooms"], ["bathrooms", "Bathrooms"], ["max_guests", "Max guests"],
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search villas…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
          sx={{ maxWidth: 300, flex: 1 }}
          inputProps={{ "data-testid": "villa-search" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }}
        />
        <Button variant="contained" startIcon={<Plus size={15} />} onClick={() => setOpen("new")} data-testid="new-admin-villa-btn">
          Add villa
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
        {["all", "pending", "approved", "rejected"].map((s) => (
          <Chip
            key={s}
            label={s === "pending" && pendingCount > 0 ? `Pending (${pendingCount})` : s[0].toUpperCase() + s.slice(1)}
            onClick={() => setStatusFilter(s)}
            data-testid={`villa-filter-${s}`}
            variant={statusFilter === s ? "filled" : "outlined"}
            sx={statusFilter === s ? { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 600 } : { fontWeight: 500 }}
          />
        ))}
      </Box>
      {filtered.length === 0 ? (
        <Box sx={CARD_SX}><EmptyState Icon={HomeIcon} label="No villas found." testId="no-admin-villas" /></Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 3 }}>
          {filtered.map((v) => (
            <Box key={v.id} data-testid={`admin-villa-${v.id}`} sx={{ ...CARD_SX, overflow: "hidden", transition: "box-shadow 0.2s ease", "&:hover": { boxShadow: 3 } }}>
              <Box sx={{ aspectRatio: "4 / 3", overflow: "hidden", position: "relative" }}>
                <Box component="img" src={v.images?.[0]} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                {v.featured && <Chip label="Featured" size="small" sx={{ position: "absolute", top: 8, left: 8, bgcolor: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 10, height: 20 }} />}
              </Box>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography className="font-serif-display" sx={{ fontSize: 18 }}>{v.name}</Typography>
                  <VillaStatusChip status={v.status} />
                </Box>
                <Typography variant="caption" color="text.secondary">{v.location}</Typography>
                <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2">₹{v.price_per_night.toLocaleString()}/night</Typography>
                  {v.discount_percent > 0 && <Chip label={`${v.discount_percent}% off`} size="small" sx={{ bgcolor: alpha("#16a34a", 0.12), color: "#16a34a", fontWeight: 600, fontSize: 10, height: 20 }} />}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, mt: 1.5 }}>
                  {v.status === "pending" && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton size="small" onClick={() => setVillaStatus(v, "approved")} data-testid={`approve-villa-${v.id}`} sx={{ width: 32, height: 32, border: 1, borderColor: "success.main", color: "success.main" }}><Check size={13} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" onClick={() => setVillaStatus(v, "rejected")} data-testid={`reject-villa-${v.id}`} sx={{ width: 32, height: 32, border: 1, borderColor: "error.main", color: "error.main" }}><X size={13} /></IconButton>
                      </Tooltip>
                    </>
                  )}
                  <EditButton onClick={() => setOpen(v)} testId={`edit-villa-${v.id}`} />
                  <ConfirmDelete label={v.name} onConfirm={() => remove(v.id)} testId={`delete-villa-${v.id}`} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha("#0ea5e9", 0.15), color: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center" }}><HomeIcon size={16} /></Box>
          <Typography className="font-serif-display" sx={{ fontSize: 22 }}>{open === "new" ? "Add villa" : "Edit villa"}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={save} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {fields.map(([k, l]) => (
                <TextField
                  key={k} label={l} required={k !== "discount_percent"}
                  value={form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  inputProps={{ "data-testid": `admin-villa-${k}` }}
                  fullWidth
                />
              ))}
            </Box>
            <TextField label="Description" required multiline rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} inputProps={{ "data-testid": "admin-villa-description" }} fullWidth />
            <TextField label="Image URLs (comma separated)" required value={form.images} onChange={(e) => setForm((f) => ({ ...f, images: e.target.value }))} inputProps={{ "data-testid": "admin-villa-images" }} fullWidth />
            <TextField label="Amenities (comma)" value={form.amenities} onChange={(e) => setForm((f) => ({ ...f, amenities: e.target.value }))} inputProps={{ "data-testid": "admin-villa-amenities" }} fullWidth />
            <TextField label="Categories (comma)" value={form.categories} onChange={(e) => setForm((f) => ({ ...f, categories: e.target.value }))} inputProps={{ "data-testid": "admin-villa-categories" }} fullWidth />
            <FormControlLabel
              control={<Switch checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} inputProps={{ "data-testid": "admin-villa-featured" }} />}
              label="Featured"
            />
            <Button type="submit" variant="contained" size="large" data-testid="save-admin-villa-btn">Save villa</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ---------- Bookings ----------

function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (b, status) => {
    try { await api.patch(`/admin/bookings/${b.id}/status`, { status }); toast(`Booking ${status}`); load(); }
    catch { toast("Could not update"); }
  };

  return (
    <Box sx={{ ...CARD_SX, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 680 }}>
        <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
          <TableRow>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Villa</Eyebrow></TableCell>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Dates</Eyebrow></TableCell>
            <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Total</Eyebrow></TableCell>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Status</Eyebrow></TableCell>
            <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Update</Eyebrow></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.length === 0 ? (
            <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><EmptyState Icon={CalIcon} label="No bookings yet." testId="no-admin-bookings" /></TableCell></TableRow>
          ) : bookings.map((b) => (
            <TableRow key={b.id} data-testid={`admin-booking-${b.id}`} sx={{ "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  {b.villa_image ? (
                    <Box component="img" src={b.villa_image} alt="" sx={{ width: 36, height: 36, borderRadius: 2, objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: (t) => alpha(t.palette.text.primary, 0.06), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><HomeIcon size={14} /></Box>
                  )}
                  {b.villa_name}
                </Box>
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{format(new Date(b.check_in), "dd MMM")} – {format(new Date(b.check_out), "dd MMM yyyy")}</TableCell>
              <TableCell align="right">₹{b.total.toLocaleString()}</TableCell>
              <TableCell data-testid={`admin-booking-status-${b.id}`}><StatusChip status={b.status} /></TableCell>
              <TableCell align="right">
                <Select
                  size="small"
                  value={b.status}
                  onChange={(e) => setStatus(b, e.target.value)}
                  inputProps={{ "data-testid": `admin-booking-status-select-${b.id}` }}
                  sx={{ minWidth: 110 }}
                >
                  {["confirmed", "approved", "declined", "cancelled"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------- Coupons ----------

function CouponsPanel() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(null); // null | "new" | coupon
  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const empty = { code: "", discount_type: "percent", amount: "", villa_id: "", active: true, usage_limit: "", expires_at: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (open && open !== "new") {
      setForm({
        ...empty, ...open,
        villa_id: open.villa_id || "",
        usage_limit: open.usage_limit ?? "",
        expires_at: open.expires_at ? open.expires_at.slice(0, 10) : "",
      });
    } else setForm(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      villa_id: form.villa_id || null,
      usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
      expires_at: form.expires_at || null,
    };
    try {
      if (open === "new") await api.post("/admin/coupons", payload);
      else await api.patch(`/admin/coupons/${open.id}`, payload);
      toast("Coupon saved"); setOpen(null); load();
    } catch (err) { toast(err.response?.data?.detail || "Could not save coupon"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); toast("Coupon deleted"); load(); }
    catch { toast("Could not delete"); }
  };

  const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <Button variant="contained" startIcon={<Percent size={15} />} onClick={() => setOpen("new")} data-testid="new-coupon-btn">
          Add coupon
        </Button>
      </Box>
      <Box sx={{ ...CARD_SX, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
            <TableRow>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Code</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Discount</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Usage</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Expires</Eyebrow></TableCell>
              <TableCell><Eyebrow sx={{ fontSize: 10 }}>Status</Eyebrow></TableCell>
              <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Actions</Eyebrow></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.length === 0 ? (
              <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><EmptyState Icon={Percent} label="No coupons yet." testId="no-coupons" /></TableCell></TableRow>
            ) : coupons.map((c) => (
              <TableRow key={c.id} data-testid={`coupon-row-${c.id}`} sx={{ "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                <TableCell>
                  <Chip label={c.code} size="small" sx={{ bgcolor: alpha("#C5A059", 0.12), color: "#C5A059", fontWeight: 700, fontFamily: "monospace" }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{c.discount_type === "percent" ? `${c.amount}%` : `₹${c.amount}`}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{c.used_count}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{c.expires_at ? format(new Date(c.expires_at), "dd MMM yyyy") : "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={!c.active ? "Inactive" : isExpired(c) ? "Expired" : "Active"}
                    size="small"
                    sx={{
                      fontWeight: 600, fontSize: 11, height: 22,
                      ...(!c.active || isExpired(c)
                        ? { bgcolor: alpha("#6b7280", 0.12), color: "#6b7280" }
                        : { bgcolor: alpha("#16a34a", 0.12), color: "#16a34a" }),
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <EditButton onClick={() => setOpen(c)} testId={`edit-coupon-${c.id}`} />
                  <Box component="span" sx={{ display: "inline-block", width: 6 }} />
                  <ConfirmDelete label={c.code} onConfirm={() => remove(c.id)} testId={`delete-coupon-${c.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha("#C5A059", 0.15), color: "#C5A059", display: "flex", alignItems: "center", justifyContent: "center" }}><Percent size={16} /></Box>
          <Typography className="font-serif-display" sx={{ fontSize: 22 }}>{open === "new" ? "Add coupon" : "Edit coupon"}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={save} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField label="Code" required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} inputProps={{ "data-testid": "coupon-code", style: { textTransform: "uppercase" } }} fullWidth />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))} inputProps={{ "data-testid": "coupon-type" }} fullWidth>
                <MenuItem value="percent">Percent off</MenuItem>
                <MenuItem value="flat">Flat amount off</MenuItem>
              </Select>
              <TextField label={form.discount_type === "percent" ? "Percent" : "Amount (₹)"} required type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} inputProps={{ "data-testid": "coupon-amount" }} fullWidth />
            </Box>
            <TextField label="Villa ID (optional — scope to one villa)" value={form.villa_id} onChange={(e) => setForm((f) => ({ ...f, villa_id: e.target.value }))} inputProps={{ "data-testid": "coupon-villa-id" }} fullWidth />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="Usage limit (optional)" type="number" value={form.usage_limit} onChange={(e) => setForm((f) => ({ ...f, usage_limit: e.target.value }))} inputProps={{ "data-testid": "coupon-usage-limit" }} fullWidth />
              <TextField label="Expires on (optional)" type="date" InputLabelProps={{ shrink: true }} value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} inputProps={{ "data-testid": "coupon-expires" }} fullWidth />
            </Box>
            <FormControlLabel
              control={<Switch checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} inputProps={{ "data-testid": "coupon-active" }} />}
              label="Active"
            />
            <Button type="submit" variant="contained" size="large" data-testid="save-coupon-btn">Save coupon</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ---------- Reviews ----------

function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const [form, setForm] = useState({ rating: 5, comment: "" });
  useEffect(() => { if (edit) setForm({ rating: edit.rating, comment: edit.comment }); }, [edit]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/reviews/${edit.id}`, { rating: Number(form.rating), comment: form.comment });
      toast("Review updated"); setEdit(null); load();
    } catch { toast("Could not update review"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/reviews/${id}`); toast("Review deleted"); load(); }
    catch { toast("Could not delete"); }
  };

  return (
    <Box sx={{ ...CARD_SX, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
          <TableRow>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>User</Eyebrow></TableCell>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Rating</Eyebrow></TableCell>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Comment</Eyebrow></TableCell>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Date</Eyebrow></TableCell>
            <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Actions</Eyebrow></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reviews.length === 0 ? (
            <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><EmptyState Icon={Star} label="No reviews yet." testId="no-reviews" /></TableCell></TableRow>
          ) : reviews.map((r) => (
            <TableRow key={r.id} data-testid={`review-row-${r.id}`} sx={{ "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar src={r.user_picture || undefined} sx={{ width: 28, height: 28, fontSize: 12 }}>{r.user_name?.[0]}</Avatar>
                  {r.user_name}
                </Box>
              </TableCell>
              <TableCell>
                <Chip icon={<Star size={12} fill="#C5A059" color="#C5A059" style={{ marginLeft: 6 }} />} label={r.rating} size="small" sx={{ bgcolor: alpha("#C5A059", 0.1), fontWeight: 600 }} />
              </TableCell>
              <TableCell sx={{ maxWidth: 320, color: "text.secondary" }}>{r.comment}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{format(new Date(r.created_at), "dd MMM yyyy")}</TableCell>
              <TableCell align="right">
                <EditButton onClick={() => setEdit(r)} testId={`edit-review-${r.id}`} />
                <Box component="span" sx={{ display: "inline-block", width: 6 }} />
                <ConfirmDelete label="this review" onConfirm={() => remove(r.id)} testId={`delete-review-${r.id}`} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha("#e11d48", 0.15), color: "#e11d48", display: "flex", alignItems: "center", justifyContent: "center" }}><Star size={16} /></Box>
          <Typography className="font-serif-display" sx={{ fontSize: 22 }}>Edit review</Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={save} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Select value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} inputProps={{ "data-testid": "review-rating" }} fullWidth>
              {[1, 2, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>{n} star{n > 1 ? "s" : ""}</MenuItem>)}
            </Select>
            <TextField label="Comment" required multiline rows={3} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} inputProps={{ "data-testid": "review-comment" }} fullWidth />
            <Button type="submit" variant="contained" size="large" data-testid="save-review-btn">Save review</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ---------- Support inbox ----------

function SupportInboxPanel() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");

  const loadThreads = () => api.get("/admin/concierge/threads").then((r) => setThreads(r.data)).catch(() => {});
  useEffect(() => { loadThreads(); }, []);

  const openThread = (bookingId) => {
    setActiveId(bookingId);
    api.get(`/admin/concierge/threads/${bookingId}`).then((r) => setMessages(r.data)).catch(() => {});
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/admin/concierge/threads/${activeId}`, { message: reply });
      setReply("");
      openThread(activeId);
      loadThreads();
    } catch { toast("Could not send reply"); }
  };

  const active = threads.find((t) => t.booking_id === activeId);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "340px 1fr" }, gap: 3, alignItems: "start" }}>
      <Box sx={{ ...CARD_SX, overflow: "hidden" }}>
        {threads.length === 0 ? (
          <EmptyState Icon={MessageCircle} label="No support threads yet." testId="no-threads" />
        ) : threads.map((t) => (
          <Box
            key={t.booking_id}
            component="button"
            type="button"
            onClick={() => openThread(t.booking_id)}
            data-testid={`thread-${t.booking_id}`}
            sx={{
              display: "block", width: "100%", textAlign: "left", p: 2, border: "none", borderBottom: 1, borderColor: "divider",
              bgcolor: activeId === t.booking_id ? (th) => alpha(th.palette.primary.main, 0.08) : "transparent",
              cursor: "pointer", font: "inherit", color: "inherit",
              "&:hover": { bgcolor: (th) => alpha(th.palette.text.primary, 0.03) },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{t.guest_name}</Typography>
              <Typography variant="caption" color="text.secondary" flexShrink={0}>{format(new Date(t.last_at), "dd MMM")}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{t.villa_name}</Typography>
            <Typography variant="caption" noWrap sx={{ display: "block", mt: 0.5, color: t.last_from === "concierge" ? "text.secondary" : "text.primary", fontWeight: t.last_from === "concierge" ? 400 : 600 }}>
              {t.last_from === "guest" ? "" : t.last_from === "owner" ? "Owner: " : "You: "}{t.last_message}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ ...CARD_SX, p: 3, minHeight: 320 }}>
        {!active ? (
          <EmptyState Icon={MessageCircle} label="Select a thread to view the conversation." testId="no-thread-selected" />
        ) : (
          <>
            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{active.guest_name} · {active.villa_name}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, my: 2.5, maxHeight: 360, overflowY: "auto" }} data-testid="thread-messages">
              {messages.map((m) => (
                <Box key={m.id} sx={{ alignSelf: m.from === "guest" ? "flex-start" : "flex-end", maxWidth: "80%" }}>
                  <Box sx={{ px: 1.5, py: 1, borderRadius: 3, bgcolor: m.from === "guest" ? (t) => alpha(t.palette.text.primary, 0.06) : alpha("#C5A059", 0.15) }}>
                    <Typography variant="body2">{m.message}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, textAlign: m.from === "guest" ? "left" : "right" }}>
                    {m.author_name} · {format(new Date(m.created_at), "dd MMM, HH:mm")}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth size="small" placeholder="Reply as Nirvagoo Concierge…"
                value={reply} onChange={(e) => setReply(e.target.value)}
                inputProps={{ "data-testid": "inbox-reply-text" }}
              />
              <Button variant="contained" startIcon={<Send size={14} />} onClick={sendReply} data-testid="inbox-reply-send">Send</Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// ---------- Content ----------

function PagesEditor() {
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const [form, setForm] = useState({ eyebrow: "", title: "", body: "", cta_label: "", cta_to: "" });
  useEffect(() => {
    if (open) setForm({ eyebrow: open.eyebrow, title: open.title, body: (open.body || []).join("\n\n"), cta_label: open.cta_label || "", cta_to: open.cta_to || "" });
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      eyebrow: form.eyebrow,
      title: form.title,
      body: form.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      cta_label: form.cta_label || null,
      cta_to: form.cta_to || null,
    };
    try {
      await api.patch(`/admin/pages/${open.slug}`, payload);
      toast("Page saved"); setOpen(null); load();
    } catch { toast("Could not save page"); }
  };

  return (
    <Box>
      {pages.length === 0 ? (
        <Box sx={CARD_SX}><EmptyState Icon={FileText} label="No pages found." testId="no-pages" /></Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 2 }}>
          {pages.map((p) => (
            <Box key={p.slug} data-testid={`page-card-${p.slug}`} sx={{ ...CARD_SX, p: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>/p/{p.slug}</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 15, mt: 0.5, mb: 1.5 }} noWrap>{p.title}</Typography>
              <EditButton onClick={() => setOpen(p)} testId={`edit-page-${p.slug}`} />
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha("#0ea5e9", 0.15), color: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center" }}><FileText size={16} /></Box>
          <Typography className="font-serif-display" sx={{ fontSize: 22 }}>Edit /p/{open?.slug}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={save} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField label="Eyebrow" required value={form.eyebrow} onChange={(e) => setForm((f) => ({ ...f, eyebrow: e.target.value }))} inputProps={{ "data-testid": "page-eyebrow" }} fullWidth />
            <TextField label="Title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} inputProps={{ "data-testid": "page-title" }} fullWidth />
            <TextField label="Body (separate paragraphs with a blank line)" required multiline rows={6} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} inputProps={{ "data-testid": "page-body" }} fullWidth />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="CTA label (optional)" value={form.cta_label} onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))} inputProps={{ "data-testid": "page-cta-label" }} fullWidth />
              <TextField label="CTA link (optional)" value={form.cta_to} onChange={(e) => setForm((f) => ({ ...f, cta_to: e.target.value }))} inputProps={{ "data-testid": "page-cta-to" }} fullWidth />
            </Box>
            <Button type="submit" variant="contained" size="large" data-testid="save-page-btn">Save page</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function SubscribersList() {
  const [subs, setSubs] = useState([]);
  const load = () => api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/admin/newsletter/${id}`); toast("Subscriber removed"); load(); }
    catch { toast("Could not remove"); }
  };

  return (
    <Box sx={{ ...CARD_SX, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 420 }}>
        <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
          <TableRow>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Email</Eyebrow></TableCell>
            <TableCell><Eyebrow sx={{ fontSize: 10 }}>Subscribed</Eyebrow></TableCell>
            <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Actions</Eyebrow></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subs.length === 0 ? (
            <TableRow><TableCell colSpan={3} sx={{ border: 0 }}><EmptyState Icon={Mail} label="No subscribers yet." testId="no-subscribers" /></TableCell></TableRow>
          ) : subs.map((s) => (
            <TableRow key={s.id} data-testid={`subscriber-row-${s.id}`} sx={{ "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
              <TableCell>{s.email}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{format(new Date(s.subscribed_at), "dd MMM yyyy")}</TableCell>
              <TableCell align="right"><ConfirmDelete label={s.email} onConfirm={() => remove(s.id)} testId={`delete-subscriber-${s.id}`} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ContentPanel() {
  const [sub, setSub] = useState("pages");
  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
        {[["pages", "Pages", FileText], ["subscribers", "Subscribers", Mail]].map(([v, label, Icon]) => (
          <Chip
            key={v}
            icon={<Icon size={13} />}
            label={label}
            onClick={() => setSub(v)}
            data-testid={`content-sub-${v}`}
            variant={sub === v ? "filled" : "outlined"}
            sx={sub === v ? { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 600, "& .MuiChip-icon": { color: "inherit" } } : { fontWeight: 500 }}
          />
        ))}
      </Box>
      {sub === "pages" ? <PagesEditor /> : <SubscribersList />}
    </Box>
  );
}

// ---------- Settings ----------

function SettingsPanel() {
  const [settings, setSettings] = useState({ commission_percent: 15 });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", { commission_percent: Number(settings.commission_percent) });
      setSettings(data);
      toast("Settings saved");
    } catch { toast("Could not save settings"); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ ...CARD_SX, p: 3, maxWidth: 440 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: alpha("#C5A059", 0.15), color: "#C5A059", display: "flex", alignItems: "center", justifyContent: "center" }}><SettingsIcon size={16} /></Box>
        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Platform settings</Typography>
      </Box>
      <Box component="form" onSubmit={save} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Owner payout commission (%)"
          type="number"
          value={settings.commission_percent}
          onChange={(e) => setSettings((s) => ({ ...s, commission_percent: e.target.value }))}
          helperText="Percentage of each booking's total kept by Nirvagoo before the owner payout."
          inputProps={{ "data-testid": "settings-commission", min: 0, max: 100 }}
          fullWidth
        />
        <Button type="submit" variant="contained" size="large" disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </Box>
    </Box>
  );
}

// ---------- Root ----------

const TITLES = {
  overview: "Overview", locations: "Locations", users: "Users", villas: "Villas",
  bookings: "Bookings", coupons: "Pricing & Offers", reviews: "Reviews",
  inbox: "Support Inbox", content: "Content", settings: "Settings",
};

export default function AdminPortal() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    users: 0, villas: 0, bookings: 0, coupons: 0, avg_rating: 0, total_revenue: 0,
    booking_status_counts: {}, revenue_trend: [],
  });
  const [tab, setTab] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, tab]);

  if (authLoading) return <Box sx={{ py: 16, textAlign: "center", fontSize: 14, color: "text.secondary" }} data-testid="admin-loading">Loading…</Box>;
  if (!user) return <Navigate to="/login?redirect=/admin" replace />;
  if (user.role !== "admin") {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", py: 12, textAlign: "center", px: 3 }} data-testid="admin-forbidden">
        <Typography className="font-serif-display" sx={{ fontSize: 30, mb: 1.5 }}>Admin access required</Typography>
        <Typography variant="body2" color="text.secondary">Sign in as an admin to manage platform data.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "70vh" }} data-testid="admin-portal">
      <Sidebar tab={tab} onSelect={setTab} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Topbar title={TITLES[tab]} onMenuClick={() => setMobileOpen(true)} user={user} />
        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <TabPanel value="overview" current={tab}><OverviewPanel stats={stats} /></TabPanel>
          <TabPanel value="locations" current={tab}><LocationsPanel /></TabPanel>
          <TabPanel value="users" current={tab}><UsersPanel /></TabPanel>
          <TabPanel value="villas" current={tab}><VillasPanel /></TabPanel>
          <TabPanel value="bookings" current={tab}><BookingsPanel /></TabPanel>
          <TabPanel value="coupons" current={tab}><CouponsPanel /></TabPanel>
          <TabPanel value="reviews" current={tab}><ReviewsPanel /></TabPanel>
          <TabPanel value="inbox" current={tab}><SupportInboxPanel /></TabPanel>
          <TabPanel value="content" current={tab}><ContentPanel /></TabPanel>
          <TabPanel value="settings" current={tab}><SettingsPanel /></TabPanel>
        </Box>
      </Box>
    </Box>
  );
}
